import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { forkJoin, of } from "rxjs";
import { Observable } from "rxjs/Observable";
import { catchError, map, switchMap } from "rxjs/operators";
import * as fromStore from "../../_store";
import { DeputyActions } from "../actions";
import { DeputyActionTypes, LoadPossibleDeputiesSuccess } from "../actions/deputy.actions";
import { Base64Utils } from '../../utils/Base64Utils';
import { DeputyService } from '../../services/deputy/deputy.service';
import { ImpersonalizationService } from '../../services/impersonalization/impersonalization.service';
import { UserGeneralDataService } from '../../services/user-general-data/user-general-data.service';



@Injectable()
export class DeputyEffects {

    @Effect()
    load$: Observable<Action> = this.actions$
        .pipe(ofType<DeputyActions.LoadSubstitute>(DeputyActionTypes.LOAD_SUBSTITUTE))
        .pipe(
            map((action: DeputyActions.LoadSubstitute) => action.username),
            switchMap((username: string) => this.deputyService.get(username).pipe(
                map((user: any) => {
                    if (user) {
                        return new DeputyActions.LoadSubstituteSuccess(user);
                    }
                    else {
                        return new DeputyActions.LoadSubstituteFailure();
                    }
                }),
                catchError(() => of(new DeputyActions.LoadSubstituteFailure()))
            )
        )
    )


    @Effect()
    loadSubstitutesPhotos$: Observable<Action> = this.actions$
    .pipe(ofType<DeputyActions.LoadSubstitutesPhotos>(DeputyActionTypes.LOAD_SUBSTITUTES_PHOTOS))
    .pipe(
        map((action: DeputyActions.LoadSubstitutesPhotos) => {
            let substituteIds: any = [];
            if (action.substitutes && action.substitutes.length > 0) {
                for (let i = 0; i < action.substitutes.length; i++) {
                    substituteIds.push(action.substitutes[i] ? action.substitutes[i].id : null);
                }
            }
            return { substituteIds } as any;
        }),
        switchMap((action: any) => {
            let requestArray = [];

            for (let i = 0; i < action.substituteIds.length; i++) {
                requestArray.push(this.http.get(this.userGeneralDataService.getPhotoRequestAddress(action.substituteIds[i])));
            }
            return forkJoin(requestArray).pipe(
                map((results: any) => {
                    let photosArray: any = [];
                    let photo: any;
                    for (let i = 0; i < results.length; i++) {
                        photo = null;
                        if (results[i].data) {
                            let mime = Base64Utils.getImgMimeType(results[i].data);
                            photo = {
                                base64: results[i].data,
                                preview: "data:" + mime + ";base64," + results[i].data
                            }
                        }
                        photosArray.push({
                            photo: photo
                        });
                    }
                    return new DeputyActions.LoadSubstitutesPhotosSuccess(photosArray);
            }), catchError(() => of(new DeputyActions.LoadSubstitutesPhotosFailure())))
        })
    )

    @Effect()
    search$: Observable<any> = this.actions$
    .pipe(ofType<DeputyActions.LoadPossibleDeputies>(DeputyActionTypes.LOAD_POSSIBLE_DEPUTIES))
    .pipe(
        switchMap(() => this.deputyService.getPossibleSubstitutes().pipe(
            map((substitutes: any) => {
                if (substitutes) {
                    substitutes.forEach((substitute: any) => {
                        if (substitute.photo) {
                            let mime = Base64Utils.getImgMimeType(substitute.photo);
                            let photo = {
                                base64: substitute.photo,
                                preview: "data:" + mime + ";base64," + substitute.photo
                            };
                            substitute.photo = photo;
                        }
                    });
                }
                if (substitutes && substitutes.length > 0) {
                    this.store.dispatch(new DeputyActions.LoadSubstitutesPhotos(substitutes));
                    return new DeputyActions.LoadPossibleDeputiesSuccess(substitutes);
                } else {
                    return new DeputyActions.LoadPossibleDeputiesFailure();
                }
            }),
            catchError(() => of(new DeputyActions.LoadPossibleDeputiesFailure())
        )))
    );


    @Effect()
    loadImpersonalizationList$: Observable<Action> = this.actions$
        .pipe(ofType<DeputyActions.LoadImpersonalizationList>(DeputyActionTypes.LOAD_IMPERSONALIZATION_LIST))
        .pipe(
            map((action: DeputyActions.LoadImpersonalizationList) => action.criteria),
            switchMap((criteria: any) => this.impersonalizationService.getImpersonalizationList(criteria).pipe(
                map((response: any) => {
                    // let user = response as any;
                    if (response) {
                        return new DeputyActions.LoadImpersonalizationListSuccess(response);
                    } else {
                        return new DeputyActions.LoadImpersonalizationListFailure(null);
                    }
                }),
                catchError(() => of(new DeputyActions.LoadImpersonalizationListFailure(null)))
            )
        )
    )
    @Effect()
    loadUsersList$: Observable<Action> = this.actions$
        .pipe(ofType<DeputyActions.LoadUsersList>(DeputyActionTypes.LOAD_USERS_LIST))
        .pipe(
            map((action: DeputyActions.LoadUsersList) => {
              return {
                businessEntityId: action.businessEntityId,
                criteria: action.criteria
              } as any;
            }),
            switchMap((action: any) => this.impersonalizationService.getUsersList(action.businessEntityId, action.criteria).pipe(
                map((users: any) => {
                    if (users) {
                        return new DeputyActions.LoadUsersListSuccess(users);
                    }
                    else {
                        return new DeputyActions.LoadUsersListFailure();
                    }
                }),
                catchError(() => of(new DeputyActions.LoadUsersListFailure()))
            )
        )
    )

    @Effect()
    ImpersonalizeUser$: Observable<Action> = this.actions$
        .pipe(ofType<DeputyActions.ImpersonalizeUser>(DeputyActionTypes.IMPERSONALIZE_USER))
        .pipe(
            map((action: DeputyActions.ImpersonalizeUser) => {
                return {
                    username: action.username,
                    businessEntityId: action.businessEntityId
                } as any;
            }),
            switchMap((action: any) => this.impersonalizationService.getImpersonalization(action.username, action.businessEntityId).pipe(
                map((impersonalization: any) => {
                    if (impersonalization) {
                        return new DeputyActions.ImpersonalizeUserSuccess(impersonalization);
                    }
                    else {
                        return new DeputyActions.ImpersonalizeUserFailure();
                    }
                }),
                catchError(() => of(new DeputyActions.ImpersonalizeUserFailure()))
            )
        )
    )

    constructor(
        private actions$: Actions,
        private deputyService: DeputyService,
        private impersonalizationService: ImpersonalizationService,
        private userGeneralDataService: UserGeneralDataService,
        private store: Store<fromStore.CoreState>,
        private http: HttpClient
    ) { }
}
